import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../hooks/useLanguage";
import { Calendar } from "react-calendar";
import { toZonedTime } from 'date-fns-tz';

import { api } from "ticketino-api-client";
import {
    CreateShopBasketOrder, GetShopBasketOrder, AddTicketsToOrder, GetEventById, GetEventsThatAreInEventGroup,
    CountEventsThatAreInEventGroup, GetTicketSoldOfEvent, GetTicketTypesByEventId
} from "../utils/api";

import Header from "./components/Header";
import Footer from "./components/Footer";

import "../css/calendar.css";

const Step30_CalendarRally = () => {
    const [regularToken, setRegularToken] = useState(null);
    const [resources, setResources] = useState({});

    const [posId, setPosId] = useState(null);
    const [eventGroupCityRallyId, setEventGroupCityRallyId] = useState(0);
    const [eventGroupWowId, setEventGroupWowId] = useState(0);

    const [orderId, setOrderId] = useState(0);
    const [order, setOrder] = useState({});

    const [eventsWithDetails, setEventsWithDetails] = useState([]);
    const [eventDates, setEventDates] = useState([]);
    const [date, setDate] = useState(new Date());

    const [isLoading, setIsLoading] = useState(true);
    const [isBooking, setIsBooking] = useState(false);

    const selectedTicketsCrossSelling = JSON.parse(localStorage.getItem('selectedTicketsCrossSelling'));
    const selectedAddOns = JSON.parse(localStorage.getItem('selectedTicketsAddons'));

    const timeZone = 'Europe/Zurich';
    const today = new Date();
    const zurichDate = toZonedTime(today, timeZone);
    const defaultMonth = zurichDate.getMonth();
    const [currentMonth, setCurrentMonth] = useState(new Date(zurichDate.getFullYear(), defaultMonth, 1));

    let navigate = useNavigate();
    let baseUrl = process.env.REACT_APP_BASEURL_API;
    api.defaults.baseURL = baseUrl;

    const { language } = useLanguage();
    const username = localStorage.getItem("username");
    const adminNewBookingUsername = sessionStorage.getItem("adminNewBookingUsername");

    useEffect(() => {
        setRegularToken(localStorage.getItem("token"));
        requestFormSettings();
    }, []);

    useEffect(() => {
        if (language) {
            requestResources();
        }
    }, [language]);

    useEffect(() => {
        if (!posId) return;

        const initializeOrder = async () => {
            let orderIdFromSession = localStorage.getItem("OrderId");

            if (orderIdFromSession) {
                try {
                    let orderResponse = await fetchOrderById(orderIdFromSession);
                    if (orderResponse && orderResponse.id) {
                        setOrderId(orderResponse.id);
                        setOrder(orderResponse);
                        return;
                    }
                } catch (err) {}
            }

            try {
                const res = await createOrder();
                await localStorage.setItem("OrderId", res.id);
                setOrderId(res.id);
                setOrder(res);
            } catch (err) {
                console.error("Failed to create order", err);
            }
        };

        initializeOrder();
    }, [posId, baseUrl]);

    useEffect(() => {
        if (currentMonth && eventGroupCityRallyId > 0) {
            loadCalendarEventDates(eventGroupCityRallyId, currentMonth);
        }
    }, [currentMonth, eventGroupCityRallyId]);

    const requestFormSettings = async () => {
        await axios.get(`form/formsettings`).then((res) => {
            setPosId(res.data.posId);
            setEventGroupCityRallyId(res.data.eventGroupCityRallyId);
            setEventGroupWowId(res.data.eventGroupId);
        });
    };

    const requestResources = async () => {
        await axios
            .get(`form/resources/${language}`)
            .then((res) => {
                setResources(res.data?.translation);
            })
            .catch((error) => console.error(error.response.data));
    };

    const loadCalendarEventDates = async (eventGroupId, currentMonth) => {
        setIsLoading(true);

        const dateFrom = new Date(Date.UTC(currentMonth.getFullYear(), currentMonth.getMonth(), 1));
        const dateTo = new Date(Date.UTC(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0));
        const dateFromISO = dateFrom.toISOString().split('T')[0];
        const dateToISO = dateTo.toISOString().split('T')[0];

        const totalNumberOfEvents = await fetchCountEventsThatAreInEventGroup(eventGroupId, dateFromISO, dateToISO);
        const eventBatchSize = 100;
        let events = [];

        for (let i = 0; i <= totalNumberOfEvents; i += eventBatchSize) {
            const eventGroupEventsResponse = await fetchEventsThatAreInEventGroup(eventGroupId, language, eventBatchSize, i, dateFromISO, dateToISO);
            events = events.concat(eventGroupEventsResponse?.events);
        }

        const selectedTicketCodes = (selectedTicketsCrossSelling?.tickets || [])
            .filter(t => !!t.externalTicketCode)
            .map(t => t.externalTicketCode);

        const totalQuantity = (selectedTicketsCrossSelling?.tickets || [])
            .reduce((sum, t) => sum + (t.quantity || 0), 0);

        const loadedEvents = (
            await Promise.all(
                events.map(async (event) => {
                    const eventDetails = await fetchEventById(event.id);
                    if (eventDetails?.status !== 1) return null;

                    const ticketCountResponse = await fetchTicketCount(event.id);
                    if ((eventDetails?.maxTickets - ticketCountResponse) < totalQuantity) return null;

                    const ticketTypesResponse = await GetTicketTypesByEventId(baseUrl, "token", regularToken, event.id);
                    const filteredTicketTypes = (ticketTypesResponse?.ticketTypes || []).filter(tt => !!tt.externalTicketCode);

                    const hasMatchingTickets = selectedTicketCodes.some(code =>
                        filteredTicketTypes.some(tt => tt.externalTicketCode === code)
                    );

                    if (!hasMatchingTickets) return null;

                    return { ...event, ticketTypes: filteredTicketTypes };
                })
            )
        ).filter(e => e !== null);

        setEventDates(loadedEvents.map(e => e.start?.split('T')[0]));
        setEventsWithDetails(loadedEvents);
        setIsLoading(false);
    };

    const createOrder = async () => {
        try {
            if (!regularToken) {
                throw new Error("No regularToken found at createOrder");
            }

            const newOrder = {
                affiliateShopId: null,
                currency: "CHF",
                tenantId: 1,
                pointOfSaleId: posId,
                abbreviation: "",
                paymentType: 1,
            };

            if (adminNewBookingUsername && adminNewBookingUsername.length > 0) {
                newOrder.email = adminNewBookingUsername;
            } else if (username && username.length > 0) {
                newOrder.email = username;
            }

            const res = await CreateShopBasketOrder(baseUrl, "token", regularToken, newOrder);
            return res;
        } catch (error) {
            console.error('Error creating order:', error);
        }
    };

    const fetchOrderById = async (orderId) => {
        try {
            if (!regularToken) {
                throw new Error("No regularToken found at fetchOrderById");
            }
            const res = await GetShopBasketOrder(baseUrl, "token", regularToken, orderId);
            return res;
        } catch (error) {
            console.error('Error fetching order by Id:', error);
        }
    };

    const fetchEventById = async (eventId) => {
        try {
            if (!regularToken) {
                throw new Error("No regularToken found at fetchEventById");
            }
            const res = await GetEventById(baseUrl, "token", regularToken, eventId);
            return res;
        } catch (error) {
            console.error('Error fetching event by Id:', error);
        }
    };

    const fetchEventsThatAreInEventGroup = async (eventGroupId, language, eventBatchSize, numberToSkip, dateFrom, dateTo) => {
        try {
            if (!regularToken) {
                throw new Error("No regularToken found at fetchEventsThatAreInEventGroup");
            }
            const res = await GetEventsThatAreInEventGroup(baseUrl, "token", regularToken, eventGroupId, language, eventBatchSize, numberToSkip, dateFrom, dateTo);
            return res;
        } catch (error) {
            console.error('Error fetching events that are in event group:', error);
        }
    };

    const fetchCountEventsThatAreInEventGroup = async (eventGroupId, dateFrom, dateTo) => {
        try {
            if (!regularToken) {
                throw new Error("No regularToken found at fetchCountEventsThatAreInEventGroup");
            }
            const res = await CountEventsThatAreInEventGroup(baseUrl, "token", regularToken, eventGroupId, dateFrom, dateTo);
            return res;
        } catch (error) {
            console.error('Error fetching count events that are in event group:', error);
        }
    };

    const fetchTicketCount = async (eventId) => {
        try {
            if (!regularToken) {
                throw new Error("No regularToken found at fetchTicketCount");
            }
            const res = await GetTicketSoldOfEvent(baseUrl, "token", regularToken, eventId);
            return res.data;
        } catch (error) {
            console.error('Error fetching ticket count:', error);
        }
    };

    const handleActiveStartDateChange = ({ activeStartDate }) => {
        setCurrentMonth(activeStartDate);
    };

    // Add-ons (goodie bag, raum vis-à-vis, etc.) are ticket types on WOW events, not City Rally events.
    // Fetch the WOW event for the selected date and resolve add-on ticketTypeIds from it.
    const resolveAddOnsFromWowEvent = async (selectedDate) => {
        if (!selectedAddOns?.tickets?.length || !eventGroupWowId) return [];

        const nextDay = new Date(selectedDate);
        nextDay.setDate(nextDay.getDate() + 1);
        const dateToISO = nextDay.toISOString().split('T')[0];

        const totalNumberOfEvents = await fetchCountEventsThatAreInEventGroup(eventGroupWowId, selectedDate, dateToISO);
        let events = [];
        for (let i = 0; i <= totalNumberOfEvents; i += 100) {
            const res = await fetchEventsThatAreInEventGroup(eventGroupWowId, language, 100, i, selectedDate, dateToISO);
            events = events.concat(res?.events || []);
        }

        const addOnCodes = selectedAddOns.tickets.map(t => t.externalTicketCode);

        for (const event of events) {
            const ticketTypesResponse = await GetTicketTypesByEventId(baseUrl, "token", regularToken, event.id);
            const ticketTypes = ticketTypesResponse?.ticketTypes || [];

            const hasMatch = addOnCodes.some(code => ticketTypes.some(tt => tt.externalTicketCode === code));
            if (!hasMatch) continue;

            return selectedAddOns.tickets.map(ticket => {
                const matchedType = ticketTypes.find(tt => tt.externalTicketCode === ticket.externalTicketCode);
                return { ...ticket, ticketTypeId: matchedType?.id };
            });
        }

        return [];
    };

    const onClickDay = async (clickedDate) => {
        if (isBooking) return;

        const formattedDate = new Date(Date.UTC(clickedDate.getFullYear(), clickedDate.getMonth(), clickedDate.getDate()))
            .toISOString().split('T')[0];

        if (!eventDates.includes(formattedDate)) return;

        setIsBooking(true);

        try {
            const event = eventsWithDetails.find(e => e.start?.split('T')[0] === formattedDate);
            if (!event) return;

            // Map rally tickets to ticketTypeIds — no timeslot time (valid entire day)
            const rallyTicketsWithTicketTypeId = (selectedTicketsCrossSelling?.tickets || []).map(ticket => {
                const matchedType = event.ticketTypes.find(tt => tt.externalTicketCode === ticket.externalTicketCode);
                return { ...ticket, ticketTypeId: matchedType?.id };
            });

            // Add-ons live on WOW events — resolve them separately
            const addOnsWithTicketTypeId = await resolveAddOnsFromWowEvent(formattedDate);

            await addTicketToBasket([...rallyTicketsWithTicketTypeId, ...addOnsWithTicketTypeId]);
        } catch (error) {
            console.error("Error during booking:", error);
        } finally {
            setIsBooking(false);
        }
    };

    const addTicketToBasket = async (selectedTicketsWithTicketTypeId) => {
        try {
            let currentOrder = await fetchOrderById(orderId);
            let targetOrderId = orderId;

            if (!currentOrder || !currentOrder.id) {
                const newOrder = await createOrder();
                if (!newOrder || !newOrder.id) {
                    console.error("Failed to create a new order.");
                    return;
                }
                setOrderId(newOrder.id);
                setOrder(newOrder);
                sessionStorage.setItem("OrderId", newOrder.id);
                targetOrderId = newOrder.id;
            } else {
                targetOrderId = currentOrder.id;
            }

            const ticketsToAdd = selectedTicketsWithTicketTypeId
                .filter(ticket => ticket.ticketTypeId && ticket.quantity > 0)
                .map(ticket => ({
                    ticketTypeId: ticket.ticketTypeId,
                    quantity: ticket.quantity,
                }));

            if (ticketsToAdd.length === 0) {
                console.warn("No valid tickets to add to basket.");
                return;
            }

            await AddTicketsToOrder(baseUrl, "token", regularToken, targetOrderId, { TicketsToAdd: ticketsToAdd });

            navigate(`/${language}/summary`);
        } catch (error) {
            console.error("Failed to add tickets to basket:", error);
        }
    };

    const tileClassName = ({ date }) => {
        const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        const formattedDate = utcDate.toISOString().split('T')[0];
        return eventDates.includes(formattedDate) ? 'bold-date' : 'disabled-date';
    };

    const formatMonth = (locale, date) => {
        return date.toLocaleDateString(locale, { month: 'long' });
    };

    return (
        <>
            <Header language={language} pagePath="calendar-rally" pageTitle={resources?.CalendarTitle}
                ticketCount={order?.tickets?.length || 0} />

            <div className="page-container">
                <div className="container wrapper">
                    <div className="row">
                        <div className="col-md-12">
                            <div className={isLoading ? "is-hidden" : ""}>
                                <Calendar
                                    view="month"
                                    value={date}
                                    onChange={(newDate) => setDate(newDate)}
                                    tileClassName={tileClassName}
                                    onClickDay={onClickDay}
                                    locale={language}
                                    minDate={new Date(zurichDate.getFullYear(), defaultMonth, 1)}
                                    onActiveStartDateChange={handleActiveStartDateChange}
                                    formatMonthYear={formatMonth}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Footer language={language} />
        </>
    );
};

export default Step30_CalendarRally;
